import { faBell } from "@fortawesome/free-solid-svg-icons/faBell";
import { faCalendarDay } from "@fortawesome/free-solid-svg-icons/faCalendarDay";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import React from "react";
import { useTranslation } from "react-i18next";
import styled, { DefaultTheme } from "styled-components";

import { TaskPriority } from "../../model/entry/types/TaskPriority.enum";
import { Rounded } from "../../theme/Rounded";
import { Spacing } from "../../theme/Spacing";
import { AtiraIcon } from "../AtiraIcon";
import { Flex } from "../Flex";
import { Text } from "../Text";
import { TextWithTruncated } from "../TextWithTruncated";
import { TaskKind } from "../../model/task/shared/TasksKind.enum";
import { getTaskPriorityColor } from "../../utils/taskUtils";
import { standardDate } from "../../utils/Date";
import { useDrawersContext } from "../../routes/tasks/drawers-context";
import { BaseTask, ContactTask, OrphanTask } from "../../model/task/Task";

const Container = styled(Flex)<{ kind?: TaskKind }>`
  padding: ${Spacing.s} ${Spacing.m};
  border-radius: ${Rounded.md};
  border-inline-start: ${({ theme, kind }) =>
    `3px solid ${theme[`${kind?.toLowerCase()}Task` as keyof DefaultTheme]}`};
  gap: ${Spacing.s};
  background-color: ${({ theme }) => theme.lightSub};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.sub};
  }
`;

const TaskTitle = styled(TextWithTruncated)`
  min-width: 0;
`;

const KindLabel = styled(Text)<{ kind: TaskKind }>`
  flex: 0 0 auto;
  color: ${({ theme, kind }) =>
    theme[`${kind.toLowerCase()}Task` as keyof DefaultTheme]};
`;

const ScheduleItem = styled(Flex)`
  align-items: center;
  gap: calc(${Spacing.s} / 2);

  svg {
    flex: 0 0 auto;
  }
`;

const PriorityText = styled(Text)<{ priority?: TaskPriority }>`
  position: relative;
  z-index: 1;
  font-weight: bold;
  color: ${({ theme, priority }) => theme[getTaskPriorityColor(priority)]};
  padding: calc(${Spacing.s} / 2) ${Spacing.s};
  border-radius: ${Rounded.pill};
  border: ${({ theme, priority }) =>
    `2px solid ${theme[getTaskPriorityColor(priority)]}`};
  font-size: ${Spacing.s};
  margin-inline-start: auto;

  &::after {
    content: "";
    inset: 0;
    position: absolute;
    border-radius: inherit;
    background-color: ${({ theme, priority }) =>
      theme[getTaskPriorityColor(priority)]};
    opacity: 0.1;
    z-index: -1;
  }
`;

type FloatingTodayTasksTaskProps = {
  task: BaseTask;
};

export const FloatingTodayTasksTask: React.FC<FloatingTodayTasksTaskProps> = ({
  task,
}) => {
  const { t } = useTranslation();
  const { setTask, setTaskReadVisible } = useDrawersContext();

  const onPreviewTask = () => {
    setTask(task);
    setTaskReadVisible(true);
  };

  return (
    <Container kind={task.kind} flexDirection="column" onClick={onPreviewTask}>
      <Flex alignItems="center" gap="s" width="100%">
        <AtiraIcon
          icon={faCircle}
          size="xs"
          color={`${task.kind.toLowerCase()}Task` as keyof DefaultTheme}
        />
        <TaskTitle length={55} fontSize="m" fontWeight="bold">
          {task.name}
        </TaskTitle>
        {[TaskKind.CONTACT, TaskKind.ORPHAN].includes(task.kind) ? (
          <PriorityText priority={(task as ContactTask | OrphanTask)?.priority}>
            {t(
              `common.${(task as ContactTask | OrphanTask)?.priority?.toLowerCase()}Priority`,
            )}
          </PriorityText>
        ) : null}
      </Flex>

      {task.description ? (
        <TextWithTruncated length={90} color="darkerSub" fontSize="s">
          {task.description}
        </TextWithTruncated>
      ) : null}

      <Flex alignItems="center" gap="m" flexWrap="wrap">
        <KindLabel kind={task.kind} fontSize="xs" fontWeight="bold">
          {t(`common.${task.kind.toLowerCase()}_task`)}
        </KindLabel>
        {task.reminder ? (
          <ScheduleItem>
            <AtiraIcon icon={faBell} size="xs" color="darkerSub" />
            <Text color="darkerSub" fontSize="xs">
              {t("common.reminder")} ·{" "}
              {standardDate<string>(task.reminder, undefined, true)}
            </Text>
          </ScheduleItem>
        ) : null}
        {task.deadline ? (
          <ScheduleItem>
            <AtiraIcon icon={faCalendarDay} size="xs" color="darkerSub" />
            <Text color="darkerSub" fontSize="xs">
              {t("common.deadline")} ·{" "}
              {standardDate<string>(task.deadline, undefined, true)}
            </Text>
          </ScheduleItem>
        ) : null}
      </Flex>
    </Container>
  );
};
